import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GlassSpinner from "../../components/GlassSpinner";
import { useEmpresa } from "../../context/EmpresaContext";
import { getEntradas, getEventos, getRRPP, getEmpresaConfig, reenviarEntrada, eliminarEntrada } from "../../services/sininApi";
import { useToast, ToastPopup } from "../../components/Toast";

const STATUS_COLORS = {
  PAID:      { bg: "#d1e7dd", color: "#0a5234" },
  PENDIENTE: { bg: "#fff3cd", color: "#856404" },
  RECHAZADO: { bg: "#f8d7da", color: "#842029" },
};

const formatIngreso = value => {
  if (!value) return "—";
  const [fecha = "", hora = ""] = String(value).trim().split(/\s+/);
  const partes = fecha.replaceAll("-", "/").split("/");
  const fechaVisible = partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : fecha;
  return `${fechaVisible}${hora ? ` ${hora.slice(0, 5)}` : ""}`;
};

const calcStats = items => ({
  total: items.length,
  ingresaron: items.filter(item => !!item.used).length,
  pendientes: items.filter(item => !item.used).length,
});

export default function ClienteInvitados() {
  const { idEvento }              = useParams();
  const navigate                  = useNavigate();
  const { empresa, colores }      = useEmpresa();
  const GOLD                      = colores.primario;
  const FUENTE                    = colores.fuente;
  const empresaId                 = empresa?.empresaId;
  const role                      = empresa?.role;

  const [entradas, setEntradas]   = useState([]);
  const [filtered, setFiltered]   = useState([]);
  const [rrpp, setRrpp]           = useState([]);
  const [evento, setEvento]       = useState(null);
  const [stats, setStats]         = useState({ total: 0, ingresaron: 0, pendientes: 0 });
  const [loading, setLoading]     = useState(true);
  const [reenvId, setReenvId]     = useState(null);
  const [mailEnabled, setMailEnabled] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null); // entry a eliminar
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const { toast, showToast, closeToast } = useToast();

  const [filters, setFilters] = useState({
    busqueda: "", tanda: "", used: "", createdBy: "",
  });

  useEffect(() => {
    Promise.all([
      getEventos(empresaId, empresa?.loginType || "0"),
      getRRPP(empresaId),
      getEmpresaConfig(empresaId),
    ]).then(([evData, rrppData, configData]) => {
      const evs = Array.isArray(evData) ? evData : [];
      setEvento(evs.find(e => e.idEvento === idEvento) || null);
      setRrpp(Array.isArray(rrppData?.items) ? rrppData.items : []);
      setMailEnabled(configData?.empresa?.mailEnabled === true);
    }).catch(() => {});
  }, [idEvento, empresaId, empresa]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEntradas(empresaId, {
        idEvento,
        createdBy: filters.createdBy || undefined,
        used:      filters.used      || undefined,
      });
      const items = Array.isArray(res.items) ? res.items : [];
      setEntradas(items);
      setStats(res.stats || calcStats(items));
    } catch { setEntradas([]); }
    setLoading(false);
  }, [idEvento, empresaId, filters.createdBy, filters.used]);

  useEffect(() => { cargar(); }, [cargar]);

  // Filtros en memoria (búsqueda texto + tanda)
  useEffect(() => {
    let list = [...entradas];
    const q = filters.busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter(i =>
        i.dni?.toLowerCase().includes(q) ||
        i.first_name?.toLowerCase().includes(q) ||
        i.last_name?.toLowerCase().includes(q) ||
        `${i.first_name} ${i.last_name}`.toLowerCase().includes(q)
      );
    }
    if (filters.tanda) {
      list = list.filter(i => i.tandaId === filters.tanda || i.tanda === filters.tanda);
    }
    if (sortConfig.key) {
      list.sort((a, b) => {
        const va = String(a[sortConfig.key] || "").toLowerCase();
        const vb = String(b[sortConfig.key] || "").toLowerCase();
        return sortConfig.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    setFiltered(list);
  }, [filters.busqueda, filters.tanda, entradas, sortConfig]);

  const setFilter   = k => e => setFilters(p => ({ ...p, [k]: e.target.value }));
  const toggleSort  = key => setSortConfig(p => ({ key, dir: p.key === key && p.dir === "asc" ? "desc" : "asc" }));
  const arrow       = key => sortConfig.key === key ? (sortConfig.dir === "asc" ? " ▲" : " ▼") : "";
  const rrppNombre  = dni => { const r = rrpp.find(x => x.dni === dni); return r ? `${r.first_name} ${r.last_name}` : dni || "—"; };

  const handleReenviar = async (hashSeguro) => {
    setReenvId(hashSeguro);
    try {
      await reenviarEntrada(empresaId, hashSeguro);
      showToast("Mail reenviado.", "success");
    } catch { showToast("Error al reenviar.", "error"); }
    setReenvId(null);
  };

  const handleEliminar = async () => {
    const g = confirmEliminar;
    setConfirmEliminar(null);
    try {
      const res = await eliminarEntrada(empresaId, g.hashSeguro, g.createdBy);
      if (res?.error) throw new Error(res.error);
      setEntradas(p => {
        const next = p.filter(e => e.hashSeguro !== g.hashSeguro);
        setStats(calcStats(next));
        return next;
      });
      showToast(
        res.mailSent
          ? "Entrada eliminada. Se avisó por email que quedó inhabilitada."
          : `Entrada eliminada. No se pudo avisar por email: ${res.mailWarning || "sin detalle"}.`,
        "success"
      );
    } catch { showToast("Error al eliminar.", "error"); }
  };

  const tandas = evento?.tandas || [];
  const styles = makeStyles(GOLD, FUENTE);

  return (
    <div style={styles.page}>
      {loading && <div style={styles.overlay}><GlassSpinner size={80} background={empresa?.colorFondo || "#1a1a1a"} /></div>}

      {/* Header */}
      <div style={{ ...styles.header, background: empresa?.colorFondo || "#1a1a1a", color: FUENTE }}>
        <button style={{ ...styles.backBtn, color: GOLD }} onClick={() => navigate("/cliente/eventos")}>← Eventos</button>
        <div style={{ fontWeight: "700", fontSize: 15, display: "flex", alignItems: "center", gap: 8, color: FUENTE }}>
          {empresa?.logoUrl && <img src={empresa.logoUrl} alt="" style={{ width: 38, height: 38, objectFit: "contain" }} />}
          {evento?.name || idEvento}
        </div>
        <button style={{ ...styles.outlineBtn, color: FUENTE, borderColor: `${FUENTE}66` }} onClick={() => navigate(`/cliente/estadisticas/${idEvento}`)}>Stats</button>
      </div>

      {/* Stats chips */}
      <div style={styles.chipsRow}>
        {[
          ["Total",      stats.total,       "#e9ecef"],
          ["Ingresaron", stats.ingresaron,   "#cfe2ff"],
          ["Pendientes", stats.pendientes,   "#fff3cd"],
        ].map(([l, v, bg]) => (
          <div key={l} style={{ ...styles.chip, background: bg }}>
            <span style={{ fontWeight: "800", fontSize: 18 }}>{v}</span>
            <span style={{ fontSize: 11, color: "#555" }}>{l}</span>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={styles.filtersRow}>
        <input
          style={styles.fi} placeholder="Buscar nombre o DNI..."
          value={filters.busqueda} onChange={setFilter("busqueda")}
        />
        <select style={styles.fi} value={filters.used} onChange={e => {
          setFilters(p => ({ ...p, used: e.target.value }));
        }}>
          <option value="">Todos</option>
          <option value="si">Ingresaron</option>
          <option value="no">No ingresaron</option>
        </select>
        {rrpp.length > 0 && (
          <select style={styles.fi} value={filters.createdBy} onChange={setFilter("createdBy")}>
            <option value="">Todos los RRPP</option>
            {rrpp.map(r => <option key={r.dni} value={r.dni}>{r.first_name} {r.last_name}</option>)}
          </select>
        )}
        {tandas.length > 1 && (
          <select style={styles.fi} value={filters.tanda} onChange={setFilter("tanda")}>
            <option value="">Todas las tandas</option>
            {tandas.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        <button style={styles.clearBtn}
          onClick={() => setFilters({ busqueda: "", tanda: "", used: "", createdBy: "" })}>
          Limpiar
        </button>
      </div>

      {/* Tabla */}
      <div style={styles.tableWrap}>
        {filtered.length === 0 ? (
          <p style={{ textAlign: "center", color: "#888", padding: 30 }}>Sin resultados.</p>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={{ background: empresa?.colorFondo || "#1a1a1a", color: GOLD }}>
                {[["first_name","Nombre"],["last_name","Apellido"],["dni","DNI"],
                  ["tanda","Tanda"],["tipoPago","Tipo"],["used","Ingreso"]].map(([k,l]) => (
                  <th key={k} style={styles.th} onClick={() => toggleSort(k)}>{l}{arrow(k)}</th>
                ))}
                <th style={styles.th}>Fecha y hora</th>
                <th style={styles.th}>RRPP</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
							  {filtered.map((g, i) => {
                  // eslint-disable-next-line
                  const sc = STATUS_COLORS[g.estado] || STATUS_COLORS.PENDIENTE;
                  return (
                    <tr
                      key={g.hashSeguro}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}
                    >
                      <td style={styles.td}>{g.first_name}</td>
                      <td style={styles.td}>{g.last_name}</td>
                      <td style={styles.td}>{g.dni}</td>
                      <td style={styles.td}>{g.tanda || "—"}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.badge,
                            background:
                              g.tipoPago === "CORTESIA" ? "#e9ecef" : "#fff3cd",
                            color: "#333",
                          }}
                        >
                          {g.tipoPago}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {g.used ? (
                          <span
                            style={{
                              ...styles.badge,
                              background: "#cfe2ff",
                              color: "#084298",
                            }}
                          >
                            ✓ {g.used.split(" ")[1] || g.used}
                          </span>
                        ) : (
                          <span style={{ color: "#aaa", fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, whiteSpace: "nowrap" }}>{formatIngreso(g.used)}</td>
                      <td style={styles.td}>{rrppNombre(g.createdBy)}</td>
                      <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {g.pdf_url && (
                            <a href={g.pdf_url} target="_blank" rel="noreferrer"
                              style={{ ...styles.reenvBtn, textDecoration: "none", fontSize: 12, padding: "5px 9px" }}
                              title="Descargar PDF">
                              ⬇️
                            </a>
                          )}
                          {role !== "rrpp" && (
                            reenvId === g.hashSeguro ? (
                              <span style={{ color: "#aaa", fontSize: 12, padding: "5px 0" }}>...</span>
                            ) : (
                              <button style={{ ...styles.reenvBtn, fontSize: 12, padding: "5px 9px" }}
                                onClick={() => handleReenviar(g.hashSeguro)} title="Reenviar por email">
                                ✉️
                              </button>
                            )
                          )}
                          {g.pdf_url && role !== "rrpp" && (
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(`Hola ${g.first_name}, te enviamos tu entrada. Descargala acá: ${g.pdf_url}`)}`}
                              target="_blank" rel="noreferrer"
                              style={{ ...styles.reenvBtn, textDecoration: "none", fontSize: 12, padding: "5px 9px",
                                background: "#25d366", color: "#fff" }}
                              title="Enviar por WhatsApp">
                              💬
                            </a>
                          )}
                          {!g.used && (
                            <button
                              style={{ ...styles.reenvBtn, fontSize: 12, padding: "5px 9px",
                                background: "#e9ecef", color: "#555" }}
                              onClick={() => setConfirmEliminar(g)}
                              title="Eliminar entrada">
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ height: 40 }} />
      <ToastPopup toast={toast} onClose={closeToast} accentColor={GOLD} accentText={empresa?.colorFuente || "#fff"} />

      {/* Popup confirmación eliminar */}
      {confirmEliminar && (
        <div style={styles.overlay} onClick={() => setConfirmEliminar(null)}>
          <div style={styles.popupCard} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 22, textAlign: "center", marginBottom: 10 }}>🗑️</div>
            <div style={{ fontWeight: "800", fontSize: 16, textAlign: "center", marginBottom: 8 }}>
              ¿Eliminar entrada?
            </div>
            <div style={{ fontSize: 13, color: "#666", textAlign: "center", marginBottom: 20 }}>
              {confirmEliminar.first_name} {confirmEliminar.last_name} — DNI {confirmEliminar.dni}
              <br />Se eliminará también el PDF del ticket.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmEliminar(null)}
                style={{ flex: 1, padding: "11px", borderRadius: 25, border: "1px solid #ddd",
                  background: "#f4f4f4", cursor: "pointer", fontWeight: "700", fontSize: 14 }}>
                Cancelar
              </button>
              <button onClick={handleEliminar}
                style={{ flex: 1, padding: "11px", borderRadius: 25, border: "none",
                  background: GOLD, color: empresa?.colorFuente || "#fff",
                  cursor: "pointer", fontWeight: "900", fontSize: 14 }}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const makeStyles = (GOLD, FUENTE) => ({
  page:        { background: "#f4f4f4", minHeight: "100vh" },
  overlay:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  popupCard:   { background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 360, boxShadow: "0 8px 28px rgba(0,0,0,0.3)" },
  header:      { background: "#1a1a1a", color: "#fff", padding: "13px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  backBtn:     { background: "transparent", color: GOLD, border: "none", cursor: "pointer", fontWeight: "600", fontSize: 14 },
  outlineBtn:  { background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 13 },
  chipsRow:    { display: "flex", gap: 8, padding: "12px 16px", flexWrap: "wrap" },
  chip:        { display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 14px", borderRadius: 10, minWidth: 70 },
  filtersRow:  { display: "flex", flexWrap: "wrap", gap: 8, padding: "0 16px 12px", background: "#fff", borderBottom: "1px solid #e0e0e0" },
  fi:          { padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, flex: 1, minWidth: 120 },
  clearBtn:    { padding: "8px 12px", borderRadius: 8, border: "none", background: "#e0e0e0", cursor: "pointer", fontSize: 13 },
  tableWrap:   { overflowX: "auto", padding: "12px 16px" },
  table:       { width: "100%", borderCollapse: "collapse", fontSize: 13, borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  th:          { padding: "11px 10px", textAlign: "left", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap" },
  td:          { padding: "9px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" },
  badge:       { padding: "3px 8px", borderRadius: 6, fontWeight: "700", fontSize: 11 },
  reenvBtn:    { background: GOLD, color: FUENTE, border: "none", borderRadius: 25, padding: "5px 10px", cursor: "pointer", fontWeight: "700", fontSize: 13 },
});
